import React, { useEffect, useMemo, useState } from "react";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { FaCar, FaFlag, FaFlagCheckered } from "react-icons/fa6";
import { IconType } from "react-icons";
import { setReplayStateParamsTimeSkip } from "../../../state/replayReducer";
import { ReplaySessionEvent } from "../../../types/replay";

// Map icon name strings to actual icon components
const iconMap: Record<string, IconType> = {
  FaFlag: FaFlag,
  FaCar: FaCar,
  FaFlagCheckered: FaFlagCheckered,
};

const flagIcons: Record<string, { icon: string; color: string }> = {
  GREEN: { icon: "FaFlag", color: "#4caf50" },
  FULL_YELLOW: { icon: "FaFlag", color: "#FFBA49" },
  SAFETY_CAR: { icon: "FaCar", color: "#f1840f" },
  RED: { icon: "FaFlag", color: "#ff4d4d" },
  FINISH: { icon: "FaFlagCheckered", color: "#ffffff" },
};

export default function ReplaySlider() {
  const dispatch = useDispatch();

  const loadingSessionEventData = useSelector(
    (state: RootState) => state.replay.loadingSessionEventData,
  );

  const replayState = useSelector(
    (state: RootState) => state.replay.replayState,
    isEqual,
  );

  const processedSessionData = useSelector(
    (state: RootState) => state.replay.processedSessionData,
    isEqual,
  );

  // Track elapsed time when replay is playing
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (replayState.playing && replayState.replayStartTime) {
      const startTime = replayState.replayStartTime;
      // Update immediately
      setElapsedTime(Date.now() - startTime);

      // Then update every 100ms
      const interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 100);

      return () => clearInterval(interval);
    }
  }, [replayState.playing, replayState.replayStartTime]);

  const activeSessionEvents = useMemo(() => {
    const selectedSessionEvents: ReplaySessionEvent[] = [];

    let activeProcessedSessionData = processedSessionData;
    // If replay is playing, use the processedSessionData from replayState instead of the global state
    if (replayState.playing) {
      activeProcessedSessionData =
        replayState?.processedSessionData ?? processedSessionData;
    }

    // Map each flag to it's icon
    for (const flag of activeProcessedSessionData?.flagStartTimes ?? []) {
      const flagInfo = flagIcons[flag.flag];
      if (flagInfo) {
        selectedSessionEvents.push({
          icon: flagInfo.icon,
          color: flagInfo.color,
          label: flag.flag,
          time: flag.dayTime,
        });
      }
    }

    // Process laps from processedSessionData

    const activeLapStartTimes = replayState.hilightedDriver
      ? activeProcessedSessionData?.lapStartTimesByDriver[
          replayState.hilightedDriver
        ]
      : activeProcessedSessionData?.lapStartTimes;
    if (activeLapStartTimes) {
      for (const [lap, time] of Object.entries(activeLapStartTimes)) {
        selectedSessionEvents.push({
          icon: lap,
          color: "#888888",
          label: "Lap",
          time: time as number,
        });
      }
    }

    // Sort events by time
    selectedSessionEvents.sort((a, b) => a.time - b.time);

    return selectedSessionEvents;
  }, [processedSessionData, replayState]);

  // Find green flag (start) and finish flag (end) times
  const { startTime, endTime, duration } = useMemo(() => {
    const greenFlag = activeSessionEvents.find((e) => e.label === "GREEN");
    const finishFlag = activeSessionEvents.find((e) => e.label === "FINISH");

    // Get last lap time as fallback for end time
    const laps = activeSessionEvents.filter((e) => e.label === "Lap");
    const lastLap = laps.length > 0 ? laps[laps.length - 1] : null;

    const start = greenFlag?.time ?? activeSessionEvents[0]?.time ?? 0;
    const end =
      finishFlag?.time ??
      lastLap?.time ??
      activeSessionEvents[activeSessionEvents.length - 1]?.time ??
      0;

    return {
      startTime: start,
      endTime: end,
      duration: end - start,
    };
  }, [activeSessionEvents]);

  // Calculate position percentage for each event
  const getEventPosition = (time: number) => {
    if (duration === 0) return 0;
    return ((time - startTime) / duration) * 100;
  };

  const getSnappedSliderValue = (
    relativeValue: number,
    sliderWidth: number,
  ) => {
    if (duration <= 0 || activeSessionEvents.length === 0) return relativeValue;

    const effectiveSliderWidth = Math.max(1, sliderWidth - 12);
    const snapThresholdTime = (8 / effectiveSliderWidth) * duration;

    // Convert relative value to absolute for comparison with event times
    const absoluteValue = relativeValue + startTime;

    const nearestEvent = activeSessionEvents.reduce((nearest, event) => {
      const currentDistance = Math.abs(event.time - absoluteValue);
      const nearestDistance = Math.abs(nearest.time - absoluteValue);
      return currentDistance < nearestDistance ? event : nearest;
    });

    // Return relative time (event.time - startTime) if snapping, otherwise return the original relative value
    return Math.abs(nearestEvent.time - absoluteValue) <= snapThresholdTime
      ? nearestEvent.time - startTime
      : relativeValue;
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const relativeValueMs = Number(e.currentTarget.value);
    const snappedRelativeValueMs = getSnappedSliderValue(
      relativeValueMs,
      e.currentTarget.clientWidth,
    );
    // Convert ms to seconds for storage
    dispatch(setReplayStateParamsTimeSkip(snappedRelativeValueMs / 1000));
  };

  // Separate events into laps (below) and flags (above), filter out events past endTime
  const { lapEvents, flagEvents } = useMemo(() => {
    const laps = activeSessionEvents.filter(
      (e) => e.label === "Lap" && e.time <= endTime,
    );
    const flags = activeSessionEvents.filter(
      (e) => e.label !== "Lap" && e.time <= endTime,
    );
    return { lapEvents: laps, flagEvents: flags };
  }, [activeSessionEvents, endTime]);

  if (
    (loadingSessionEventData && !replayState.playing) ||
    replayState.loadingReplay
  ) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  if (activeSessionEvents.length === 0 && !replayState.playing) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          Select a session to view timeline
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.timelineContainer}>
        <div className={styles.markersContainer}>
          {flagEvents.map((event, index) => {
            const position = getEventPosition(event.time);
            const iconName = event.icon as string;
            const IconComponent = iconMap[iconName];
            return (
              <div
                key={`flag-${event.label}-${event.time}-${index}`}
                className={styles.marker}
                style={{ left: `${position}%` }}
              >
                {IconComponent && (
                  <div
                    className={styles.markerIcon}
                    style={{ color: event.color }}
                  >
                    <IconComponent />
                  </div>
                )}
                <div className={styles.markerLine} />
              </div>
            );
          })}
        </div>

        <div className={styles.sliderWrapper}>
          <input
            type="range"
            min={0}
            max={duration}
            value={
              replayState.playing && replayState.replayStartTime
                ? elapsedTime
                : (replayState.params.timeSkip || 0) * 1000
            }
            onChange={handleSliderChange}
            className={styles.slider}
            disabled={replayState.playing}
          />
        </div>

        <div className={styles.markersContainerBelow}>
          {lapEvents.map((event, index) => {
            const position = getEventPosition(event.time);
            return (
              <div
                key={`lap-${event.time}-${index}`}
                className={styles.markerBelow}
                style={{ left: `${position}%` }}
              >
                <div className={styles.markerLineBelow} />
                <span className={styles.lapNumber}>{event.icon as string}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
